"use client"

import { useState, useEffect, useRef } from "react"
import { useNavigate } from "react-router-dom"
import { Box, CircularProgress, TextField, InputAdornment, Button, Typography, Paper } from "@mui/material"
import { Add, Search, Visibility } from "@mui/icons-material"
import { useNotification } from "../../contexts/NotificationContext"
import { useAuth } from "../../contexts/AuthContext"
import PageHeader from "../../components/common/PageHeader"
import DataTable from "../../components/common/DataTable"
import EmptyState from "../../components/common/EmptyState"
import InfiniteScroll from 'react-infinite-scroll-component';
import { getSubscriptions, getSubscriptionsPaginated } from "../../services/subscription.service"
import StatusBadge from "../../components/common/StatusBadge"

const Subscriptions = () => {
  const navigate = useNavigate()
  const { showNotification } = useNotification()
  const { user, selectedOrganization } = useAuth()
  const [subscriptions, setSubscriptions] = useState([])
  const [loading, setLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState("")
  const [searching, setSearching] = useState(false)
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const limit = 20; // You can adjust this as needed
  const [isFetching, setIsFetching] = useState(false);
  const [noMoreData, setNoMoreData] = useState(false);
  const [showLoadMore, setShowLoadMore] = useState(false);
  const searchTimeoutRef = useRef(null);

  const organizationId = selectedOrganization?._id || user?.organizationId

  useEffect(() => {
    if (organizationId) {
      setSubscriptions([]);
      setPage(1);
      setHasMore(true);
      fetchSubscriptionsPaginated(1, true);
    }
  }, [organizationId])

  const fetchSubscriptionsPaginated = async (pageToFetch = 1, reset = false, query = "") => {
    setIsFetching(true);
    setLoading(true);
    try {
      const params = { organizationId, page: pageToFetch, limit};
      if(query) {
        params.search = query;
      }
      const response = await getSubscriptionsPaginated(params);
      let newSubscriptions = response.data.map((subscription) => ({ id: subscription._id, ...subscription }));
      setTotalCount(response.count || 0);
      if (reset) {
        setSubscriptions(newSubscriptions);
        setNoMoreData(false);
        setShowLoadMore(false);
      } else {
        setSubscriptions((prev) => {
          const existingIds = prev.map((subscription) => subscription.id);
          newSubscriptions = newSubscriptions.filter((subscription) => !existingIds.includes(subscription.id));
          return [...prev, ...newSubscriptions];
        });
      }
      if (newSubscriptions.length < limit) {
        setNoMoreData(true);
        setShowLoadMore(true);
      } else {
        setNoMoreData(false);
        setShowLoadMore(false);
      }
      setHasMore(!showLoadMore);
    } catch (error) {
      showNotification('Failed to fetch subscriptions', 'error');
    } finally {
      setLoading(false);
      setIsFetching(false);
    }
  };

  const fetchNextPage = () => {
    if (isFetching || showLoadMore) return;
    setPage((prevPage) => {
      let nextPage = prevPage;
      if(totalCount >= limit) {
        nextPage = prevPage + 1;
      }
      fetchSubscriptionsPaginated(nextPage, false, searchQuery);
      return nextPage;
    });
  };

  const handleLoadMore = () => {
    setShowLoadMore(false);
    setPage((prevPage) => {
      let nextPage = prevPage;
      if(totalCount >= limit) {
        nextPage = prevPage + 1;
      }
      fetchSubscriptionsPaginated(nextPage, false, searchQuery);
      return nextPage;
    });
  };

  const handleSearch = (e) => {
    const query = e.target.value;
    setSearchQuery(query);

    clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => {
      setPage(1);
      setSubscriptions([]);
      setNoMoreData(false);
      setShowLoadMore(false);
      fetchSubscriptionsPaginated(1, true, query);
    }, 500);
  };

  const handleCreateSubscription = () => {
    navigate("/customers")
  }

  const handleViewSubscription = (subscription) => {
    // Check if customerId is an object (populated) or a string (ID only)
    const customerId =
      typeof subscription.customerId === "object" && subscription.customerId !== null
        ? subscription.customerId._id
        : subscription.customerId

    navigate(`/subscriptions/customer/${customerId}`)
  }

  const formatCurrency = (amount, currency = "USD") => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency,
    }).format(amount)
  }

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString()
  }

  const columns = [
    {
      id: "customerId",
      label: "Customer",
      minWidth: 170,
      format: (value) => value?.name || "Unknown Customer",
    },
    {
      id: "planId",
      label: "Plan",
      minWidth: 150,
      format: (value) => value?.name || "Unknown Plan",
    },
    {
      id: "status",
      label: "Status",
      minWidth: 120,
      format: (value) => <StatusBadge status={value} />,
    },
    {
      id: "price",
      label: "Price",
      minWidth: 120,
      format: (value, row) => formatCurrency(value, row.currency),
    },
    {
      id: "billingCycle",
      label: "Billing Cycle",
      minWidth: 120,
      format: (value) => value.charAt(0).toUpperCase() + value.slice(1),
    },
    {
      id: "currentPeriodStart",
      label: "Current Period",
      minWidth: 170,
      format: (value, row) => `${formatDate(value)} - ${formatDate(row.currentPeriodEnd)}`,
    },
    {
      id: "actions",
      label: "Actions",
      minWidth: 100,
      format: (_, row) => (
        <Button
          size="small"
          startIcon={<Visibility />}
          onClick={(e) => {
            e.stopPropagation()
            handleViewSubscription(row)
          }}
        >
          View
        </Button>
      ),
    },
  ]

  const SCROLLABLE_TABLE_ID = 'subscriptions-table-scrollable';

  return (
    <Box>
      <PageHeader
        title="Subscriptions"
        subtitle="Manage customer subscriptions"
        breadcrumbs={[{ text: "Dashboard", link: "/dashboard" }, { text: "Subscriptions" }]}
        action
        actionText="Create Subscription"
        actionIcon={<Add />}
        onActionClick={handleCreateSubscription}
      />

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="body1" gutterBottom>
          View and manage all customer subscriptions for your organization. To create a new subscription, first select a
          customer.
        </Typography>
      </Paper>

      <Box sx={{ mb: 3 }}>
        <TextField
          fullWidth
          placeholder="Search subscriptions..."
          value={searchQuery}
          onChange={handleSearch}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
            endAdornment: searching ? (
              <InputAdornment position="end">
                <CircularProgress size={20} />
              </InputAdornment>
            ) : null,
          }}
        />
      </Box>

      {loading && subscriptions.length === 0 ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
          <CircularProgress />
        </Box>
      ) : subscriptions.length === 0 ? (
        <EmptyState
          title="No Subscriptions Found"
          description="Get started by creating a subscription for a customer."
          actionText="Create Subscription"
          actionIcon={<Add />}
          onActionClick={handleCreateSubscription}
        />
      ) : (
        <Box id={SCROLLABLE_TABLE_ID} sx={{ height: 600, overflow: 'auto' }}>
          <InfiniteScroll
            dataLength={subscriptions.length}
            next={fetchNextPage}
            hasMore={!showLoadMore}
            loader={isFetching ? (<Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}><CircularProgress /></Box>) : null}
            endMessage={
              showLoadMore && (
                <Box sx={{ textAlign: 'center', my: 2, color: 'text.secondary' }}>
                  <b>No more subscriptions to load.</b>
                  <Box sx={{ mt: 2 }}>
                    <button onClick={handleLoadMore} style={{ padding: '8px 16px', borderRadius: 4, border: 'none', background: '#1976d2', color: '#fff', cursor: 'pointer' }}>
                      Load More
                    </button>
                  </Box>
                </Box>
              )
            }
            scrollableTarget={SCROLLABLE_TABLE_ID}
            scrollThreshold={0.98}
          >
            <DataTable 
              columns={columns} 
              data={subscriptions} 
              title="Subscriptions" 
              onRowClick={handleViewSubscription} 
              rowsPerPageOptions={[subscriptions.length]} 
              defaultRowsPerPage={subscriptions.length} 
              hidePagination={true} 
            />
          </InfiniteScroll>
        </Box>
      )}
    </Box>
  )
}

export default Subscriptions

